import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { useDeleteOwner } from "../../hooks/users";
import useAuth from "../../hooks/useAuth";
import { CustomInput } from "../atoms/CustomInput";
import { CustomButton } from "../atoms/CustomButton";
import { ModalShell } from "../atoms/ModalShell";
import { HiUserRemove } from "react-icons/hi";
import {
  useDeleteWidgetCallbot,
  useGetEntityPreferencesByPath,
} from "../../hooks/entity";

export default function DeleteOwnerModal({
  showDeleteModal,
  setShowDeleteModal,
}: {
  showDeleteModal: boolean;
  setShowDeleteModal: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  const schema = Joi.object({
    confirm: Joi.string()
      .trim()
      .required()
      .valid("CONFIRM")
      .messages({
        "any.only": t("dashboard.deleteOwner.key7") as string,
      }),
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(schema),
  });
  const { user, customer_identification } = useAuth();

  const { data: res, isLoading: isGettingPreferences } =
    useGetEntityPreferencesByPath(
      customer_identification,
      user.entity_identification,
      "lmc_form.widget.bot_id"
    );

  const { mutate, isLoading } = useDeleteOwner();
  const { mutate: deleteWidget, isLoading: isDeleting } =
    useDeleteWidgetCallbot();

  const onSubmit = (data: any) => {
    const entityIdentification = user?.entity_identification;
    const userIdentification = user?.user_identification;
    mutate(
      {
        entity_identification: entityIdentification,
        user_identification: userIdentification,
        customer_identification: customer_identification,
        validation: data.confirm,
      },
      {
        onSuccess: () => {
          if (res?.lmc_form?.widget?.bot_id) {
            deleteWidget({
              customer_identification: customer_identification,
              entity_identification: entityIdentification,
              bot_id: res.lmc_form.widget.bot_id,
            });
          }
        },
      }
    );
  };

  return (
    <ModalShell
      show={showDeleteModal}
      onClose={() => setShowDeleteModal(false)}
      onSubmit={handleSubmit(onSubmit)}
      footer={
        <>
          <CustomButton
            onClick={() => setShowDeleteModal(false)}
            disabled={isLoading}
            title={t("common.cancel") as string}
          />
          <CustomButton
            variant="danger"
            type="submit"
            disabled={isLoading || isGettingPreferences || isDeleting}
            isLoading={isLoading || isGettingPreferences || isDeleting}
            title={t("common.delete") as string}
          />
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <HiUserRemove size={24} className="text-red-500" />
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-gray-900 text-lg font-semibold leading-7">
            {t("dashboard.deleteOwner.key1")}
          </div>
          <div className="text-gray-500 text-sm leading-tight">
            {t("dashboard.deleteOwner.key2")}
          </div>
        </div>

        <CustomInput
          name="confirm"
          control={control}
          error={errors.confirm}
          helperText={errors.confirm?.message}
          onPaste={(e) => {
            e.preventDefault();
            return false;
          }}
          autoComplete="off"
          placeholder={"CONFIRM"}
        />
      </div>
    </ModalShell>
  );
}
